import React, { useImperativeHandle, forwardRef, useEffect, useState } from "react";
import { db } from "../../../config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import "./MergedTable.css";
import { useTreeView } from "../../../context/treeViewContext";
import { chunk } from "lodash";

// モーダルコンポーネントのインポート
import ModalElectric from "../../Edit/tab01/modalElectric/modalElectric";
import ModalAir from "../../Edit/tab01/modalAir/modalAir";
import ModalWater from "../../Edit/tab01/modalWater/modalWater";
import ModalNetwork from "../../Edit/tab01/modalNetwork/modalNetwork";

// Firestoreの各コレクション定義
const firestoreCollections = [
  { collectionName: "01-1_electricTable", fieldName: "electricName", colorClass: "electric", displayName: "電気", filterKey: "electric", type: "electricType" },
  { collectionName: "02-1_networkTable", fieldName: "networkName", colorClass: "network", displayName: "ネットワーク", filterKey: "network", type: "networkType" },
  { collectionName: "03-1_airTable", fieldName: "airName", colorClass: "air", displayName: "給排気", filterKey: "air", type: "airType" },
  { collectionName: "04-1_waterTable", fieldName: "waterName", colorClass: "water", displayName: "給排水", filterKey: "water", type: "waterType" }
];

const MergedTable = forwardRef(({ selectedRow, setSelectedRow }, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  useImperativeHandle(ref, () => ({
      triggerEdit() {
          if (selectedRow) {
              setIsEditing(true);
          }
      },
  }));

  const navigate = useNavigate();
  const { selectedAndChildIDList, deviceNameMap } = useTreeView();
  const currentProjectID = localStorage.getItem("currentProjectID");

  // 状態管理
  const [mergedData, setMergedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 各フィルターのON/OFF管理
  const [selectedFilters, setSelectedFilters] = useState({
    electric: true,
    air: true,
    water: true,
    network: true
  });
  const [selectedData, setSelectedData] = useState(null);
  const [mode, setMode] = useState("create"); // 'edit' or 'create'

  // データ取得処理（masterIDリストに応じて各コレクションから取得）
  const fetchData = async (idList) => {
  if (!idList || idList.length === 0) {
    setMergedData([]);
    setFilteredData([]);
    return;
  }

  setLoading(true);
  let allData = [];

  try {
    for (const { collectionName, fieldName, filterKey, displayName, colorClass, type } of firestoreCollections) {
      const chunks = idList.length === 1
        ? [[idList[0]]] // 単一値なら1つの配列として扱う
        : chunk(idList, 30); // 30件ずつ分割

      for (const idChunk of chunks) {
        const constraints = [
          idChunk.length === 1
            ? where("masterID", "==", idChunk[0])
            : where("masterID", "in", idChunk),
          where("deleted", "==", false),
          where("projectID", "==", currentProjectID)
        ];
        const q = query(collection(db, collectionName), ...constraints);
        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          masterID: doc.data().masterID || "N/A",
          name: doc.data()[fieldName] || "N/A",
          type: doc.data()[type] || "N/A",
          collectionName,
          colorClass,
          ...doc.data()
        }));

        allData.push(...data);
      }
    }

    setMergedData(allData);
  } catch (error) {
    console.error("Firestore データの取得に失敗しました:", error);
  } finally {
    setLoading(false);
  }
};

  // フィルター適用処理（色でフィルター）
  const filterData = (allData, filters) => {
    if (!allData) return;
    const filtered = allData.filter(item => filters[item.colorClass]);
    setFilteredData(filtered);
  };

  // データ取得：IDリストが変わった時のみ
  useEffect(() => {
    fetchData(selectedAndChildIDList);
  }, [selectedAndChildIDList]);

  // フィルター適用：データ or フィルター条件が変化したとき
  useEffect(() => {
    filterData(mergedData, selectedFilters);
  }, [mergedData, selectedFilters]);

  // // 編集ボタン：選択行のデータを編集画面へ遷移させる
  // const handleEditClick = () => {
  //   if (!selectedRow) return;
  //   navigate(`/EditMaster/${selectedRow.masterID}`, {
  //     state: {
  //       edit: true,
  //       data: selectedRow,
  //       category: selectedRow.colorClass,
  //       openModal: true,
  //       initialTab: "01"
  //     }
  //   });
  // };

  // 行クリック：選択行の状態更新
  const handleRowClick = (masterID, data) => {
    setSelectedRow({ masterID, ...data });
  };

  // フィルターボタンのトグル動作
  const toggleFilter = (filterKey) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  return (
    <>
      {/* フィルター + 編集ボタンエリア */}
      <div className="header-container">
        <div className="filter-area">
          {firestoreCollections.map(({ filterKey, displayName }) => (
            <button
              key={filterKey}
              className={`filter-button ${filterKey} ${selectedFilters[filterKey] ? "active" : ""}`}
              onClick={() => toggleFilter(filterKey)}
            >
              {displayName}
            </button>
          ))}
        </div>
        {/* <div className="button-area">
          <button className="edit-button" onClick={handleEditClick} disabled={!selectedRow}>
            編集
          </button>
        </div> */}
      </div>

      {/* テーブル表示エリア */}
      <div>
        {loading ? (
          <p className="loading">データ取得中...</p>
        ) : (
          <table border="1" className="utilityTable">
            <thead>
              <tr>
                <th>ID</th>
                <th>装置名</th>
                <th>ユーティリティID</th>
                <th>名称</th>
                <th>種類</th>
                <th>更新者</th>
                <th>更新日</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr
                    key={item.id}
                    className={`${item.colorClass}${selectedRow?.id === item.id ? " selected" : ""}`}
                    onClick={() => handleRowClick(item.masterID, item)}
                  >
                    <td>{item.masterID}</td>
                    <td>{deviceNameMap[item.masterID] || "名称なし"}</td>
                    <td>{item.uttID}</td>
                    <td>{item.name}</td>
                    <td>{item.type}</td>
                    <td>{item.updatedBy}</td>
                    <td>{item.updatedAt?.toDate().toLocaleDateString() || ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">データがありません</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
});

export default MergedTable;
